'use client';

import React, { useCallback, useEffect, useState } from "react";
import { AiOutlineMinus, AiOutlinePlus } from "react-icons/ai";

interface CounterProps {
  title: string;
  subtitle?: string;
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
}

const Counter: React.FC<CounterProps> = ({
  title,
  subtitle,
  value,
  onChange,
  disabled
}) => {
  const [editableValue, setEditableValue] = useState(value);

  useEffect(() => {
    setEditableValue(value);
  }, [value]);

  const onAdd = useCallback(() => {
    if (!disabled) {
      setEditableValue(editableValue + 1);
      onChange(editableValue + 1);
    }
  }, [editableValue, onChange, disabled]);

  const onReduce = useCallback(() => {
    if (!disabled && editableValue > 0) {
      setEditableValue(editableValue - 1);
      onChange(editableValue - 1);
    }
  }, [editableValue, onChange, disabled]);

  const onInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!disabled) {
        const inputValue = parseInt(event.target.value);
        if (!isNaN(inputValue)) {
          setEditableValue(inputValue);
          onChange(inputValue);
        }
      }
    },
    [onChange, disabled]
  );

  return (
    <div className="flex sm:flex-row flex-col sm:items-center justify-between sm:ml-0 ml-2">
      <div className="flex flex-col">
        <div className="font-medium text-sm sm:mr-2">{title}</div>
        {subtitle && (
          <div className="font-light text-sm text-gray-600 sm:mr-2 mt-1 mb-1">{subtitle}</div>
        )}
      </div>
      <div className="flex flex-row items-center sm:gap-2 gap-3">
        <div
          onClick={onReduce}
          className="sm:w-6 sm:h-6 w-5 h-5 rounded-full border-[1px] border-neutral-400 flex items-center justify-center text-neutral-600 cursor-pointer hover:opacity-80 transition"
        >
          <AiOutlineMinus size={16} />
        </div>
        <input
          type="number"
          min="0"
          value={editableValue}
          onChange={onInputChange}
          className="sm:w-12 sm:h-7 w-10 h-6 rounded-md border-[1px] border-neutral-400 text-center text-neutral-600 appearance-none"
          inputMode="numeric"
          disabled={disabled}
        />
        <div
          onClick={onAdd}
          className="sm:w-6 sm:h-6 w-5 h-5 rounded-full border-[1px] border-neutral-400 flex items-center justify-center text-neutral-600 cursor-pointer hover:opacity-80 transition"
        >
          <AiOutlinePlus size={16} />
        </div>
      </div>
    </div>
  );
};

export default Counter;
